// src/pages/EditEmployeePage.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
  deleteField,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, agencyFunctions, storage } from '../firebase';
import { useAuth } from '../context/AuthContext';
import styles from '../styles/FormPage.module.css';

// NEW: cropper & HEIC converter
import Cropper from 'react-easy-crop';
import heic2any from 'heic2any';

const DEFAULT_AVATAR = '/default-avatar.png';

const ROLES = [
  'Manager',
  'Sales Manager',
  'Sales',
  'Accounts Manager',
  'Designer',
  'Photographer',
  'Marketer',
  'HR',
  'Content Creator',
  'Freelancer',
];

const formatDateForInput = (value) => {
  if (!value) return '';
  try {
    if (value?.toDate) {
      const d = value.toDate();
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    }
    if (typeof value === 'string') {
      const d = new Date(value.includes('T') ? value : `${value}T00:00:00`);
      if (Number.isNaN(d.getTime())) return '';
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    }
    if (value instanceof Date) {
      const y = value.getFullYear();
      const m = String(value.getMonth() + 1).padStart(2, '0');
      const day = String(value.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    }
    return '';
  } catch {
    return '';
  }
};

/** Convert HEIC/HEIF to JPEG Blob (quality ~0.92) */
async function convertHeicToJpeg(file) {
  const result = await heic2any({
    blob: file,
    toType: 'image/jpeg',
    quality: 0.92,
  });
  // heic2any returns a Blob or an array of Blobs
  return Array.isArray(result) ? result[0] : result;
}

/** Create a cropped, square, 400x400 JPEG blob from an image source using crop pixels */
async function getCroppedSquareBlobFromSource({
  imageSrc,            // object URL or data URL
  cropPixels,          // { width, height, x, y } from react-easy-crop
  rotation = 0,        // degrees
  outputSize = 400,    // final square size
  mime = 'image/jpeg',
  quality = 0.92,
}) {
  // Load image (bitmap is fast & precise)
  const createSrc = async () => {
    const res = await fetch(imageSrc);
    const blob = await res.blob();
    // Prefer createImageBitmap if available
    if ('createImageBitmap' in window && typeof createImageBitmap === 'function') {
      return await createImageBitmap(blob);
    }
    // Fallback <img>
    const url = URL.createObjectURL(blob);
    const img = await new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = reject;
      i.src = url;
    });
    URL.revokeObjectURL(url);
    return img;
  };

  const src = await createSrc();

  // Canvas to extract the crop region (apply rotation)
  const tmpCanvas = document.createElement('canvas');
  const tctx = tmpCanvas.getContext('2d');

  // To handle rotation without clipping, we draw on a temp canvas
  // sized to fit the rotated crop region, then draw to final 400x400.
  const { x, y, width: cw, height: ch } = cropPixels;

  // Set temp canvas to crop size (before rotation)
  tmpCanvas.width = cw;
  tmpCanvas.height = ch;

  // Draw the crop region from the source
  tctx.save();
  // Move origin to top-left of crop box and draw
  tctx.drawImage(
    src,
    x, y, cw, ch,       // from source
    0, 0, cw, ch        // to temp canvas
  );
  tctx.restore();

  // If rotation is needed, rotate on another temp canvas
  let rotatedCanvas = tmpCanvas;
  if (rotation && rotation % 360 !== 0) {
    const rads = (rotation * Math.PI) / 180;
    const sin = Math.abs(Math.sin(rads));
    const cos = Math.abs(Math.cos(rads));

    const rotCanvas = document.createElement('canvas');
    rotCanvas.width = Math.floor(cw * cos + ch * sin);
    rotCanvas.height = Math.floor(cw * sin + ch * cos);
    const rctx = rotCanvas.getContext('2d');

    rctx.translate(rotCanvas.width / 2, rotCanvas.height / 2);
    rctx.rotate(rads);
    rctx.drawImage(tmpCanvas, -cw / 2, -ch / 2);
    rotatedCanvas = rotCanvas;
  }

  // Now fit/cover to final 400x400 (center-crop to square)
  const finalCanvas = document.createElement('canvas');
  finalCanvas.width = outputSize;
  finalCanvas.height = outputSize;
  const fctx = finalCanvas.getContext('2d', { alpha: true });
  fctx.imageSmoothingEnabled = true;
  fctx.imageSmoothingQuality = 'high';

  const rw = rotatedCanvas.width;
  const rh = rotatedCanvas.height;
  const rAspect = rw / rh;

  let sx = 0, sy = 0, sw = rw, sh = rh;
  if (rAspect > 1) {
    // wider than tall => crop sides
    sh = rh;
    sw = rh; // make square using height
    sx = Math.floor((rw - sw) / 2);
  } else if (rAspect < 1) {
    // taller than wide => crop top/bottom
    sw = rw;
    sh = rw; // make square using width
    sy = Math.floor((rh - sh) / 2);
  }
  fctx.drawImage(rotatedCanvas, sx, sy, sw, sh, 0, 0, outputSize, outputSize);

  const blob = await new Promise((resolve) =>
    finalCanvas.toBlob((b) => resolve(b), mime, quality)
  );
  return blob;
}

const EditEmployeePage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { currentUser, userRole } = useAuth();

  // Primary form state
  const [name, setName] = useState('');
  const [email, setEmail] = useState(''); // read-only
  const [role, setRole] = useState('');
  const [phone, setPhone] = useState('');
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [employmentStatus, setEmploymentStatus] = useState('Active');
  const [photoURL, setPhotoURL] = useState(DEFAULT_AVATAR);

  // Avatar state (cropped+resized)
  const [resizedAvatarBlob, setResizedAvatarBlob] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(DEFAULT_AVATAR);

  // Cropper modal state
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [imageToCropURL, setImageToCropURL] = useState(null); // object URL of selected (or converted) image
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  // Helpers
  const [initialData, setInitialData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  // Password reset UI
  const [resettingPassword, setResettingPassword] = useState(false);
  const [resetError, setResetError] = useState('');
  const [resetSuccess, setResetSuccess] = useState('');

  // Messages
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const canChangeEmployment = userRole === 'Manager';

  // Fetch employee
  useEffect(() => {
    const run = async () => {
      if (!userId) {
        setError('No Employee ID provided.');
        setLoading(false);
        return;
      }
      setLoading(true);
      setError('');
      setResetError('');
      setResetSuccess('');
      try {
        const refUser = doc(db, 'users', userId);
        const snap = await getDoc(refUser);
        if (!snap.exists()) {
          setError('Employee not found.');
          setInitialData(null);
          return;
        }
        const data = snap.data() || {};
        setName(data.name || '');
        setEmail(data.email || '');
        setRole(data.role || '');
        setPhone(data.phone || data.phoneNumber || '');
        setWhatsappNumber(data.whatsappNumber || '');
        setDateOfBirth(formatDateForInput(data.dateOfBirth));
        setEmploymentStatus(
          data.employmentStatus || (data.active === false ? 'Terminated' : 'Active')
        );
        const p = data.photoURL || DEFAULT_AVATAR;
        setPhotoURL(p);
        setAvatarPreview(p);

        setInitialData({
          name: data.name || '',
          role: data.role || '',
          phone: data.phone || data.phoneNumber || '',
          whatsappNumber: data.whatsappNumber || '',
          dateOfBirth: formatDateForInput(data.dateOfBirth),
          employmentStatus:
            data.employmentStatus || (data.active === false ? 'Terminated' : 'Active'),
          photoURL: p,
        });
      } catch (e) {
        console.error('Error fetching employee:', e);
        setError('Failed to load employee data.');
        setInitialData(null);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [userId]);

  // Detect changes
  const hasProfileChanged = useMemo(() => {
    if (!initialData) return false;
    const baseChanged =
      name !== initialData.name ||
      role !== initialData.role ||
      phone !== initialData.phone ||
      whatsappNumber !== initialData.whatsappNumber ||
      dateOfBirth !== initialData.dateOfBirth ||
      employmentStatus !== initialData.employmentStatus;
    const avatarChanged = !!resizedAvatarBlob;
    return baseChanged || avatarChanged;
  }, [initialData, name, role, phone, whatsappNumber, dateOfBirth, employmentStatus, resizedAvatarBlob]);

  // When user finishes cropping
  const onCropComplete = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  // File select: open cropper and handle HEIC if needed
  const onAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/') && !/\.heic$|\.heif$/i.test(file.name)) {
      setError('Please select an image file.');
      return;
    }
    if (file.size > 30 * 1024 * 1024) {
      setError('Image too large (max 30MB).');
      return;
    }

    setError('');
    try {
      let workingFile = file;

      // HEIC/HEIF -> convert to JPEG first
      const isHeic = file.type === 'image/heic' || file.type === 'image/heif' || /\.heic$|\.heif$/i.test(file.name);
      if (isHeic) {
        workingFile = await convertHeicToJpeg(file);
      }

      // Create an object URL for the cropper
      const url = URL.createObjectURL(workingFile);

      // Open cropper modal
      setImageToCropURL((prev) => {
        if (prev && prev !== DEFAULT_AVATAR) URL.revokeObjectURL(prev);
        return url;
      });
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setRotation(0);
      setCroppedAreaPixels(null);
      setCropModalOpen(true);
    } catch (err) {
      console.error('Avatar pre-process failed:', err);
      setError('Failed to open image. Try a different picture.');
    }
  };

  // Apply crop -> produce 400×400 JPEG blob + preview
  const applyCrop = async () => {
    try {
      if (!imageToCropURL || !croppedAreaPixels) {
        setError('No crop region selected.');
        return;
      }
      const blob = await getCroppedSquareBlobFromSource({
        imageSrc: imageToCropURL,
        cropPixels: croppedAreaPixels,
        rotation,
        outputSize: 400,
        mime: 'image/jpeg',
        quality: 0.92,
      });

      const previewUrl = URL.createObjectURL(blob);
      // Update state used by your existing save flow
      setResizedAvatarBlob(blob);
      setAvatarPreview(previewUrl);

      // Close modal & cleanup
      setCropModalOpen(false);
      if (imageToCropURL) {
        URL.revokeObjectURL(imageToCropURL);
        setImageToCropURL(null);
      }
    } catch (err) {
      console.error('Apply crop failed:', err);
      setError('Failed to crop image.');
    }
  };

  const cancelCrop = () => {
    setCropModalOpen(false);
    if (imageToCropURL) {
      URL.revokeObjectURL(imageToCropURL);
      setImageToCropURL(null);
    }
  };

  // Save
  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    if (!initialData) {
      setError('No baseline data to compare.');
      return;
    }
    if (!hasProfileChanged) {
      setError('No changes made.');
      return;
    }
    if (!name.trim()) {
      setError('Name cannot be empty.');
      return;
    }
    if (!ROLES.includes(role)) {
      setError('Please choose a valid role.');
      return;
    }

    setUpdating(true);
    try {
      let nextPhotoURL = photoURL;

      // Upload cropped avatar if present
      if (resizedAvatarBlob) {
        const avatarRef = ref(storage, `avatars/${userId}/profile.jpg`);
        const uploaded = await uploadBytes(avatarRef, resizedAvatarBlob, {
          contentType: resizedAvatarBlob.type || 'image/jpeg',
        });
        nextPhotoURL = await getDownloadURL(uploaded.ref);
      }

      const updates = {
        name: name.trim(),
        role,
        phone: phone || deleteField(),
        whatsappNumber: whatsappNumber.trim() || deleteField(),
        dateOfBirth: dateOfBirth || deleteField(),
        photoURL: nextPhotoURL || deleteField(),
        updatedAt: serverTimestamp(),
      };

      // Employment status (Manager only)
      if (canChangeEmployment) {
        if (employmentStatus === 'Terminated') {
          updates.employmentStatus = 'Terminated';
          updates.active = false;
          updates.terminatedAt = serverTimestamp();
          updates.terminatedBy = currentUser?.uid || 'system';
        } else {
          updates.employmentStatus = 'Active';
          updates.active = true;
          updates.terminatedAt = deleteField();
          updates.terminatedBy = deleteField();
          updates.terminationReason = deleteField();
        }
      }

      await updateDoc(doc(db, 'users', userId), updates);

      // Refresh baseline
      setInitialData({
        name,
        role,
        phone,
        whatsappNumber,
        dateOfBirth,
        employmentStatus,
        photoURL: nextPhotoURL,
      });
      setPhotoURL(nextPhotoURL);
      setAvatarPreview(nextPhotoURL);
      setResizedAvatarBlob(null);

      setSuccessMessage('Employee details updated successfully!');
      setTimeout(() => setSuccessMessage(''), 2500);
    } catch (err) {
      console.error('Error updating employee:', err);
      setError('Failed to update employee details.');
    } finally {
      setUpdating(false);
    }
  };

  // Password reset
  const [passwordBusy, setPasswordBusy] = useState(false);
  const handlePasswordReset = async () => {
    setResetError('');
    setResetSuccess('');
    if (!email) {
      setResetError('Missing user email.');
      return;
    }
    setResettingPassword(true);
    setPasswordBusy(true);
    try {
      await currentUser?.getIdToken?.(true);
      const timeout = new Promise((_, rej) => setTimeout(() => rej(new Error('timeout')), 12000));
      const triggerReset = httpsCallable(agencyFunctions, 'triggerPasswordReset');
      const res = await Promise.race([triggerReset({ targetUserEmail: email }), timeout]);

      if (res?.data?.success) {
        setResetSuccess(res.data.message || 'Password reset email sent.');
      } else {
        throw new Error(res?.data?.error || 'Unknown error');
      }
    } catch (err) {
      console.error('Password reset error:', err);
      if (err?.code === 'permission-denied') {
        setResetError("You don't have admin privileges.");
      } else if (err?.code === 'unauthenticated') {
        setResetError('Session expired - please login again.');
      } else if (String(err?.message || '').includes('timeout')) {
        setResetError('Server timeout - try again later.');
      } else {
        setResetError('Password reset failed.');
      }
    } finally {
      setResettingPassword(false);
      setPasswordBusy(false);
    }
  };

  if (loading) {
    return <div className={styles.loadingIndicator}>Loading employee data...</div>;
  }

  if (error && !initialData) {
    return (
      <div className={styles.formContainer}>
        <h2 className={styles.title}>Edit Employee</h2>
        <p className={styles.errorMessage}>{error}</p>
        <button
          onClick={() => navigate('/employees')}
          className={`${styles.formButton} ${styles.cancelButton}`}
        >
          Back to List
        </button>
      </div>
    );
  }

  if (!initialData) {
    return <div className={styles.loadingIndicator}>Employee data not available.</div>;
  }

  return (
    <div className={styles.formContainer}>
      <h2 className={styles.title}>
        Edit Employee: {initialData?.name || email || userId}
      </h2>

      {/* Profile Update Form */}
      <form onSubmit={handleProfileSubmit} className={styles.form}>
        <h3 className={styles.subTitle}>Employee Details</h3>

        {error && <p className={styles.errorMessage}>{error}</p>}
        {successMessage && <p className={styles.successMessage}>{successMessage}</p>}

        {/* Avatar */}
        <div className={styles.inputGroup}>
          <label>Avatar</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <img
              src={avatarPreview || DEFAULT_AVATAR}
              alt={name || 'Employee Avatar'}
              width={72}
              height={72}
              style={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                objectFit: 'cover',
                boxShadow: '0 0 0 2px #fff, 0 0 0 3px #e5e7eb',
              }}
              onError={(e) => {
                if (e.currentTarget.src !== DEFAULT_AVATAR) {
                  e.currentTarget.src = DEFAULT_AVATAR;
                }
              }}
            />
            <div>
              <input
                type="file"
                accept="image/*,.heic,.heif"
                onChange={onAvatarChange}
                disabled={updating}
              />
              <small>Crop as you like; saved as 400×400 JPEG</small>
            </div>
          </div>
        </div>

        {/* Email (read-only) */}
        <div className={styles.inputGroup}>
          <label htmlFor="email">Email</label>
          <input type="email" id="email" value={email} readOnly disabled className={styles.readOnlyInput} />
        </div>

        {/* Name */}
        <div className={styles.inputGroup}>
          <label htmlFor="name">Full Name</label>
          <input
            id="name"
            type="text"
            value={name}
            disabled={updating}
            onChange={(e) => setName(e.target.value)}
            required
            placeholder="Employee full name"
          />
        </div>

        {/* Role */}
        <div className={styles.inputGroup}>
          <label htmlFor="role">Role</label>
          <select
            id="role"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            required
            disabled={updating}
          >
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </div>

        {/* Phone */}
        <div className={styles.inputGroup}>
          <label htmlFor="phone">Phone</label>
          <input
            id="phone"
            type="tel"
            value={phone}
            disabled={updating}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="Optional phone number"
          />
        </div>

        {/* WhatsApp Number */}
        <div className={styles.inputGroup}>
          <label htmlFor="whatsapp">WhatsApp Number</label>
          <input
            id="whatsapp"
            type="tel"
            value={whatsappNumber}
            disabled={updating}
            onChange={(e) => setWhatsappNumber(e.target.value)}
            placeholder="e.g. +970501234567"
          />
          <small className={styles.helpText}>Format: +[country code][number] — used for task notifications</small>
        </div>

        {/* Date of Birth */}
        <div className={styles.inputGroup}>
          <label htmlFor="dob">Date of Birth</label>
          <input
            id="dob"
            type="date"
            value={dateOfBirth}
            onChange={(e) => setDateOfBirth(e.target.value)}
            disabled={updating}
          />
        </div>

        {/* Employment Status (Manager only) */}
        <div className={styles.inputGroup}>
          <label htmlFor="status">Employment Status</label>
          <select
            id="status"
            value={employmentStatus}
            onChange={(e) => setEmploymentStatus(e.target.value)}
            disabled={updating || !canChangeEmployment}
            title={!canChangeEmployment ? 'Only Managers can change employment status' : undefined}
          >
            <option value="Active">Active</option>
            <option value="Terminated">Fired</option>
          </select>
          {!canChangeEmployment && (
            <small className={styles.helpText}>Only Managers can change this.</small>
          )}
        </div>

        {/* Buttons */}
        <div className={styles.buttonGroup}>
          <button
            type="button"
            onClick={() => navigate('/employees')}
            className={`${styles.formButton} ${styles.cancelButton}`}
            disabled={updating}
          >
            Cancel
          </button>
          <button
            type="submit"
            className={`${styles.formButton} ${styles.submitButton}`}
            disabled={updating || !hasProfileChanged}
          >
            {updating ? 'Saving…' : 'Save Details'}
          </button>
        </div>
      </form>

      {/* Password Reset */}
      <div className={styles.passwordResetSection}>
        <h3 className={styles.subTitle}>Password Management</h3>
        {resetError && <p className={styles.errorMessage}>{resetError}</p>}
        {resetSuccess && <p className={styles.successMessage}>{resetSuccess}</p>}
        <p className={styles.infoText}>
          Send a password reset email to <strong>{email}</strong>. They’ll receive a link to set a new password.
        </p>
        <button
          type="button"
          onClick={handlePasswordReset}
          className={`${styles.formButton} ${styles.resetButton}`}
          disabled={resettingPassword || !email || passwordBusy}
        >
          {resettingPassword ? 'Sending…' : 'Send Password Reset Link'}
        </button>
      </div>

      {/* --- Crop Modal --- */}
      {cropModalOpen && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999
          }}
        >
          <div
            style={{
              background: '#111', color: '#fff', borderRadius: 12, width: 'min(92vw, 720px)',
              maxWidth: 720, padding: 16, boxShadow: '0 10px 30px rgba(0,0,0,0.5)'
            }}
          >
            <h3 style={{ margin: '0 0 12px' }}>Crop Avatar</h3>
            <div style={{ position: 'relative', width: '100%', height: 400, background: '#000', borderRadius: 8, overflow: 'hidden' }}>
              <Cropper
                image={imageToCropURL}
                crop={crop}
                zoom={zoom}
                rotation={rotation}
                aspect={1}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onRotationChange={setRotation}
                onCropComplete={onCropComplete}
                restrictPosition
              />
            </div>

            {/* Controls */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
              <div>
                <label style={{ fontSize: 12, opacity: 0.8 }}>Zoom</label>
                <input
                  type="range" min={1} max={4} step={0.01}
                  value={zoom} onChange={(e) => setZoom(parseFloat(e.target.value))}
                  style={{ width: '100%' }}
                />
              </div>
              <div>
                <label style={{ fontSize: 12, opacity: 0.8 }}>Rotation</label>
                <input
                  type="range" min={-180} max={180} step={1}
                  value={rotation} onChange={(e) => setRotation(parseInt(e.target.value, 10))}
                  style={{ width: '100%' }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8, marginTop: 14, justifyContent: 'flex-end' }}>
              <button
                onClick={cancelCrop}
                className={`${styles.formButton} ${styles.cancelButton}`}
              >
                Cancel
              </button>
              <button
                onClick={applyCrop}
                className={`${styles.formButton} ${styles.submitButton}`}
              >
                Apply Crop
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditEmployeePage;
